import React from 'react';

interface IconOption {
    value: string;
    label: string;
    icon: React.ReactElement<{ color?: string }>;
    isPro?: boolean;
}

interface IconSelectorCompactProps {
    value: string;
    onChange: (value: string) => void;
    options: IconOption[];
    title?: string;
    layout?: 'grid' | 'flex';
    gridCols?: number;
    itemClassName?: string;
    iconColor?: string;
}

export const IconSelectorCompact: React.FC<IconSelectorCompactProps> = ({ 
    value, 
    onChange, 
    options,
    title,
    layout = 'flex',
    gridCols = 3,
    itemClassName = '',
    iconColor = 'var(--color-black-20)'
}) => {
    const containerClassName = layout === 'grid'
        ? 'grid gap-2'
        : 'flex flex-wrap gap-2';

    const itemBaseClassName = layout === 'grid'
        ? 'flex flex-col items-center p-4'
        : 'flex items-center gap-2 px-4 py-2';

    const iconClassName = layout === 'grid'
        ? 'w-12 h-8 mb-2'
        : 'w-4 h-4';

    // Clone icons with the specified color
    const optionsWithColor = options.map(option => ({
        ...option,
        icon: React.isValidElement(option.icon) 
            ? React.cloneElement(option.icon, { color: iconColor })
            : option.icon
    }));

    return (
        <div className="flex flex-col gap-2">
            {title && <div className="text-base">{title}</div>}
            <div
                className={containerClassName}
                style={layout === 'grid' ? { gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` } : undefined}
            >
                {optionsWithColor.map((option) => (
                    <button
                        key={option.value}
                        onClick={() => onChange(option.value)}
                        className={`
                            ${itemBaseClassName} ${itemClassName}
                            transition-[border-color,background-color] duration-150 border bg-white dark:bg-neutral-800 dark:border-neutral-600
                            focus:outline-none
                            ${value === option.value
                                ? 'border-[var(--trebble-primary)] !bg-[var(--trebble-primary-20)] hover:!bg-[var(--trebble-primary-20)]'
                                : 'border-gray-200 hover:border-[var(--trebble-primary)] hover:bg-[var(--trebble-primary-0)]'
                            }
                        `}
                    >
                        <div className={iconClassName}>
                            {option.icon}
                        </div>
                        {layout === 'grid' ? (
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-sm font-medium my-2">{option.label}</span>
                                {option.isPro && (
                                    <span className="text-xs px-1.5 py-0.5 bg-[var(--trebble-primary-20)] text-[var(--trebble-primary)]">
                                        Pro
                                    </span>
                                )}
                            </div>
                        ) : (
                            <>
                                <span className="text-sm font-medium my-2">{option.label}</span>
                                {option.isPro && (
                                    <span className="text-xs px-1.5 py-0.5 bg-[var(--trebble-primary-20)] text-[var(--trebble-primary)]">
                                        Pro
                                    </span>
                                )}
                            </>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}; 